'use client';

import { c as _c } from "react/compiler-runtime";
import './volunteer-template.scss';
import React from 'react';
import { useTranslation } from 'react-i18next';
import type { FlexibleContentProps } from '@sola/schema/lib/schema.ts';
import type { VolunteerLocationItemDto } from '@sola/skippy-search-sdk/lib/schema.ts';
import { TrackVolunteerViewContent } from '@sola/tracking/components/datalayer.tsx';
import { ShareButtons } from '../../molecules/share-buttons/share-buttons.tsx';
import { VolunteerControls } from '../../molecules/volunteer-controls/volunteer-controls.tsx';
import { AccordionMain } from '../../organisms-cms/accordion/main/accordion-main.tsx';
import { BasicContentDuo } from '../../organisms-cms/basic-content/duo/basic-content-duo.tsx';
import { BasicContentSolo } from '../../organisms-cms/basic-content/solo/basic-content-solo.tsx';
import { ExpandedContentTabbed } from '../../organisms-cms/expanded-content/tabbed/expandedContent-tabbed.tsx';
import { FlexibleSection } from '../../organisms/flexible-section/flexible-section.tsx';
import { FSmeta, FSmetaFullBleedLight, FSmetaLight } from '../../utils/section-meta.ts';
import { buildLocationMedia, buildShareSummary, formatEventDateRange, getContentPositionsForRoles, hasMatchingContentPosition } from '../../utils/volunteer-utils.ts';
export interface VolunteerTourTemplateProps {
  location: VolunteerLocationItemDto;
  tourLocations?: VolunteerLocationItemDto[];
  content?: Partial<Record<'roles' | 'faq' | 'questions', FlexibleContentProps>>;
}
export const VolunteerTourTemplate: React.FC<VolunteerTourTemplateProps> = t0 => {
  const $ = _c(58);
  const {
    location,
    tourLocations,
    content
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = ["volunteers"];
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    t
  } = useTranslation(t1);
  const tourTitle = location.tourName || location.locationName;
  let t2;
  if ($[1] !== content || $[2] !== tourLocations) {
    const allTourRoles = [...new Set((tourLocations ?? []).flatMap(_temp))];
    const tourContentPositions = getContentPositionsForRoles(allTourRoles);
    t2 = content?.roles ? {
      ...content.roles,
      items: content.roles.items?.filter(item => hasMatchingContentPosition(item.contentPosition, tourContentPositions))
    } : content?.roles;
    $[1] = content;
    $[2] = tourLocations;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const roles = t2;
  let t3;
  if ($[4] !== location || $[5] !== tourLocations || $[6] !== tourTitle) {
    t3 = <TrackVolunteerViewContent location={location} tourLocations={tourLocations} title={tourTitle} />;
    $[4] = location;
    $[5] = tourLocations;
    $[6] = tourTitle;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = {
      ...FSmeta
    };
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== location) {
    t5 = buildLocationMedia(location);
    $[9] = location;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] !== t5) {
    t6 = <FlexibleSection meta={t4} children1={<BasicContentDuo media={t5} alt={true} />} />;
    $[11] = t5;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  let t7;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = {
      ...FSmeta,
      anchor: "intro",
      layoutRatio: "2-1",
      overlap: true
    };
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  let t8;
  if ($[14] !== location) {
    t8 = formatEventDateRange(location, true);
    $[14] = location;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  let t9;
  if ($[16] !== location || $[17] !== t || $[18] !== tourTitle) {
    t9 = buildShareSummary(t, {
      location,
      title: tourTitle
    });
    $[16] = location;
    $[17] = t;
    $[18] = tourTitle;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  let t10;
  if ($[20] !== t9 || $[21] !== tourTitle) {
    t10 = <ShareButtons shareTitle={tourTitle} shareSummary={t9} />;
    $[20] = t9;
    $[21] = tourTitle;
    $[22] = t10;
  } else {
    t10 = $[22];
  }
  let t11;
  if ($[23] !== t10 || $[24] !== t8 || $[25] !== tourTitle) {
    t11 = <BasicContentSolo seoLevel={1} subtitle={t8} title={tourTitle} programmaticButtons={t10} />;
    $[23] = t10;
    $[24] = t8;
    $[25] = tourTitle;
    $[26] = t11;
  } else {
    t11 = $[26];
  }
  let t12;
  if ($[27] !== t) {
    t12 = t("volunteers:aboutThisTour");
    $[27] = t;
    $[28] = t12;
  } else {
    t12 = $[28];
  }
  const t13 = location.tourDescription ?? location.description;
  let t14;
  if ($[29] !== t13) {
    t14 = <p>{t13}</p>;
    $[29] = t13;
    $[30] = t14;
  } else {
    t14 = $[30];
  }
  let t15;
  if ($[31] !== t12 || $[32] !== t14) {
    t15 = <BasicContentSolo seoLevel={2} title={t12} body={t14} />;
    $[31] = t12;
    $[32] = t14;
    $[33] = t15;
  } else {
    t15 = $[33];
  }
  let t16;
  if ($[34] !== t11 || $[35] !== t15) {
    t16 = <>{t11}{t15}</>;
    $[34] = t11;
    $[35] = t15;
    $[36] = t16;
  } else {
    t16 = $[36];
  }
  let t17;
  if ($[37] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = <></>;
    $[37] = t17;
  } else {
    t17 = $[37];
  }
  let t18;
  if ($[38] !== t16) {
    t18 = <FlexibleSection meta={t7} children1={t16} children2={t17} />;
    $[38] = t16;
    $[39] = t18;
  } else {
    t18 = $[39];
  }
  let t19;
  if ($[40] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = {
      ...FSmeta,
      anchor: "events",
      overlap: true
    };
    $[40] = t19;
  } else {
    t19 = $[40];
  }
  const t20 = location.slug ?? "";
  let t21;
  if ($[41] !== t20) {
    t21 = <FlexibleSection meta={t19} children1={<div className="component"><VolunteerControls seoLevel={2} variant="tour" slug={t20} /></div>} />;
    $[41] = t20;
    $[42] = t21;
  } else {
    t21 = $[42];
  }
  let t22;
  if ($[43] !== roles) {
    t22 = <FlexibleSection meta={FSmetaFullBleedLight} children1={roles?.items && roles.items.length > 0 ? <ExpandedContentTabbed seoLevel={2} {...roles} /> : <BasicContentSolo seoLevel={2} {...roles} />} />;
    $[43] = roles;
    $[44] = t22;
  } else {
    t22 = $[44];
  }
  const t23 = content?.faq;
  let t24;
  if ($[45] !== t23) {
    t24 = <FlexibleSection meta={FSmeta} children1={<AccordionMain seoLevel={2} {...t23} />} />;
    $[45] = t23;
    $[46] = t24;
  } else {
    t24 = $[46];
  }
  let t25;
  if ($[47] === Symbol.for("react.memo_cache_sentinel")) {
    t25 = {
      ...FSmetaLight,
      overlap: true
    };
    $[47] = t25;
  } else {
    t25 = $[47];
  }
  const t26 = content?.questions;
  let t27;
  if ($[48] !== t26) {
    t27 = <FlexibleSection meta={t25} children1={<BasicContentSolo alt={true} seoLevel={3} {...t26} />} />;
    $[48] = t26;
    $[49] = t27;
  } else {
    t27 = $[49];
  }
  let t28;
  if ($[50] !== t18 || $[51] !== t21 || $[52] !== t22 || $[53] !== t24 || $[54] !== t27 || $[55] !== t3 || $[56] !== t6) {
    t28 = <div className="VolunteerTour">{t3}{t6}{t18}{t21}{t22}{t24}{t27}</div>;
    $[50] = t18;
    $[51] = t21;
    $[52] = t22;
    $[53] = t24;
    $[54] = t27;
    $[55] = t3;
    $[56] = t6;
    $[57] = t28;
  } else {
    t28 = $[57];
  }
  return t28;
};
function _temp(loc) {
  return loc.roles ?? [];
}